import React, { useEffect, useRef, useState } from 'react';
import KeyboardArrowDownOutlinedIcon from '@mui/icons-material/KeyboardArrowDownOutlined';
import KeyboardArrowUpOutlinedIcon from '@mui/icons-material/KeyboardArrowUpOutlined';
import { matchesWithPrefix } from 'Utilities/permissionsV3Utils';
import './ActionMenu.scss';

export type ActionMenuItem = {
  label: string;
  onClick: () => void;
  /** All listed permissions must be satisfied for this item to be visible. Omit to always show. */
  permissions?: string[];
  /** When true, the item stays visible but cannot be clicked (e.g. a precondition isn't met yet). */
  disabled?: boolean;
};

type ActionMenuProps = {
  label: string;
  items: ActionMenuItem[];
  /** The caller's granted permission set. Items with permissions are hidden when not satisfied. */
  grantedPermissions?: Set<string>;
};

const ActionMenu: React.FC<ActionMenuProps> = ({ label, items, grantedPermissions = new Set() }) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const visibleItems = items.filter(({ permissions }) =>
    !permissions || permissions.every((p) => matchesWithPrefix(p, grantedPermissions))
  );

  const isDisabled = visibleItems.length === 0;

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="action-menu" ref={containerRef}>
      <button
        className={`action-menu__trigger${isOpen ? ' action-menu__trigger--open' : ''}${isDisabled ? ' action-menu__trigger--disabled' : ''}`}
        disabled={isDisabled}
        onClick={() => setIsOpen((prev) => !prev)}
      >
        <span>{label}</span>
        {isOpen ? <KeyboardArrowUpOutlinedIcon /> : <KeyboardArrowDownOutlinedIcon />}
      </button>

      {isOpen && (
        <ul className="action-menu__dropdown">
          {visibleItems.map((item) => (
            <li
              key={item.label}
              className={`action-menu__item${item.disabled ? ' action-menu__item--disabled' : ''}`}
              onClick={() => {
                if (item.disabled) return;
                item.onClick();
                setIsOpen(false);
              }}
            >
              {item.label}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default ActionMenu;
